import type { RidePassengerInfo } from '@backoffice-frontend/ad-passenger-list'
import { INFO_NOT_AVAILABLE_NA } from '@backoffice-frontend/common'
import { Divider, MoiaCard, MoiaTag } from '@backoffice-frontend/patterns'
import {
  AccessibilityIcon,
  CheckCircleSolidIcon,
  ChildseatIcon,
  ClockDashedIcon,
  ClockIcon,
  MinusCircleSolidIcon,
  WheelchairIcon,
  XCircleSolidIcon,
} from '@moia-dev/pace-icons'
import { Badge, Heading, Text } from '@moia-dev/pace-web'
import { isPast } from 'date-fns'
import type { TFunction } from 'i18next'
import type { PropsWithChildren, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  AdVehicleStreamPassengerCardCustomerInfoFragment,
  AdVehicleStreamPassengerCardSeatInfoFragment,
  AdVehicleStreamPassengerCardStopInfoFragment,
} from './PassengerCard.hook'
import { PickupOrDropOffTime } from './PickupTime'

export enum AuthenticationStatus {
  UPCOMING = 'UPCOMING',
  CANCELLED = 'CANCELLED',
  CONFIRMED = 'CONFIRMED',
  IN_PROGRESS = 'IN_PROGRESS',
  MANUAL_NO_SHOW_PENDING = 'MANUAL_NO_SHOW_PENDING',
  MANUAL_NO_SHOW_REQUESTED = 'MANUAL_NO_SHOW_REQUESTED',
  MANUAL_PASSENGER_CHECK_IN_PENDING = 'MANUAL_PASSENGER_CHECK_IN_PENDING',
  NO_SHOW_TRIGGERED = 'NO_SHOW_TRIGGERED',
}

type PassengerCardProps = {
  actionText?: ReactNode
  actionButton?: ReactNode
  customer: AdVehicleStreamPassengerCardCustomerInfoFragment | null
  ridePassengerInfo?: RidePassengerInfo | null
  waypointStop: AdVehicleStreamPassengerCardStopInfoFragment | null
  stopServiceAreaId?: string | null
  disabled?: boolean
  eta: Date
  seats: AdVehicleStreamPassengerCardSeatInfoFragment
  authenticationStatus?: AuthenticationStatus
  isNoShow: boolean
  isBoarded: boolean
  variant: 'disabled' | 'info' | 'error' | 'success' | 'warning' | 'accent'
}

export const PassengerCard = ({
  actionText,
  actionButton,
  customer,
  ridePassengerInfo,
  waypointStop,
  stopServiceAreaId,
  disabled = false,
  eta,
  seats,
  variant,
  authenticationStatus,
  isNoShow,
  isBoarded,
  children,
}: PropsWithChildren<PassengerCardProps>) => {
  const { t } = useTranslation()

  return (
    <MoiaCard highlight={variant} strongHighlight compact variant="outlined">
      <div
        css={theme => ({
          padding: theme.space.Space4,
        })}
      >
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <div
            css={theme => ({
              display: 'flex',
              alignItems: 'center',
              gap: theme.space.Space2,
            })}
          >
            <Heading
              size="md"
              css={theme => ({
                color: theme.semantic.ColorSurfaceContrastDefault,
              })}
            >
              {customer?.firstName && customer.lastName
                ? `${customer.firstName} ${customer.lastName}`
                : (ridePassengerInfo?.displayName ?? INFO_NOT_AVAILABLE_NA)}
            </Heading>
            <div
              css={theme => ({
                minWidth: theme.space.Space6,
                height: theme.space.Space6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: theme.radius.RadiusSmall,
                backgroundColor:
                  theme.semantic.ColorAccessoryReducedAlternative,
              })}
            >
              <Text>{seats.seats}</Text>
            </div>
          </div>
          <div
            css={theme => ({
              display: 'flex',
              alignItems: 'center',
              gap: theme.space.Space1,
            })}
          >
            {seats.wheelchairs > 0 && (
              <MoiaTag
                color="info"
                icon={<WheelchairIcon aria-label="Wheelchair" />}
              />
            )}
            {seats.childSeats + seats.boosterSeats > 0 && (
              <MoiaTag
                color="info"
                icon={<ChildseatIcon aria-label="Child seat" />}
              />
            )}
            {getBadge(
              getBadgeType(eta, isBoarded, isNoShow, authenticationStatus),
              t,
            )}
          </div>
        </div>

        <div
          css={theme => ({
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: theme.space.Space3,
          })}
        >
          <div
            css={theme => ({
              display: 'flex',
              alignItems: 'center',
              gap: theme.space.Space1,
              flexWrap: 'wrap',
            })}
          >
            {customer?.extendedWalkingTime && (
              <MoiaTag color="warning" icon={<AccessibilityIcon />}>
                {t('map_tag_mobilityImpaired_label')}
              </MoiaTag>
            )}
            {(customer?.visualImpairment ??
              ridePassengerInfo?.impairments?.visual) && (
              <MoiaTag color="warning" icon={<AccessibilityIcon />}>
                {t('map_tag_visuallyImpaired_label')}
              </MoiaTag>
            )}
          </div>

          <PickupOrDropOffTime
            isDropOff={isBoarded}
            isCardDisabled={disabled}
            eta={eta}
            waypointStop={waypointStop}
            stopServiceAreaId={stopServiceAreaId}
          />
        </div>

        {children && (
          <>
            <Divider spacing={2} />
            {children}
          </>
        )}
      </div>

      {(actionText || actionButton) && (
        <div
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: actionText ? 'space-between' : 'flex-end',
            gap: theme.space.Space2,
            backgroundColor: theme.semantic.ColorAccessoryReducedAlternative,
            borderBottomRightRadius: theme.space.Space2,
            borderBottomLeftRadius: theme.space.Space2,
            padding: theme.space.Space4,
          })}
        >
          {actionText && <div>{actionText}</div>}
          {actionButton && <div css={{ minWidth: 0 }}>{actionButton}</div>}
        </div>
      )}
    </MoiaCard>
  )
}

const getBadge = (badgeType: BadgeType, t: TFunction) => {
  const badges = {
    [BadgeType.UPCOMING]: (
      <Badge
        variant="neutral"
        label={t('adFleetMap_upcoming_label')}
        icon={<ClockDashedIcon />}
      />
    ),
    [BadgeType.PENDING]: (
      <Badge
        variant="neutral"
        label={t('adFleetMap_pending_label')}
        icon={<ClockDashedIcon />}
      />
    ),
    [BadgeType.OVERDUE]: (
      <Badge
        key="no-show-requested"
        label={t('adFleetMap_overdue_label')}
        variant={'warning'}
        icon={<ClockIcon />}
      />
    ),
    [BadgeType.CHECKED_IN]: (
      <Badge
        key="success"
        label={t('adFleetMap_checkedIn_label')}
        variant={'success'}
        icon={<CheckCircleSolidIcon />}
      />
    ),
    [BadgeType.BOARDED]: (
      <Badge
        key="success"
        label={t('adFleetMap_boarded_label')}
        variant={'success'}
        icon={<CheckCircleSolidIcon />}
      />
    ),
    [BadgeType.NO_SHOW]: (
      <Badge
        key="no-show"
        label={t('adFleetMap_noShow_label')}
        variant={'critical'}
        icon={<XCircleSolidIcon />}
      />
    ),
    [BadgeType.CANCELLED]: (
      <Badge
        key="cancelled"
        label={t('common_cancelled_label')}
        variant={'critical'}
        icon={<MinusCircleSolidIcon />}
      />
    ),
    [BadgeType.NA]: (
      <Badge
        key="cancelled"
        label={t('common_notApplicable_label')}
        variant={'neutral'}
      />
    ),
  }
  return badges[badgeType]
}

export const getBadgeType = (
  eta: Date,
  isBoarded: boolean,
  isNoShow: boolean,
  authenticationStatus?: AuthenticationStatus,
): BadgeType => {
  const isUpcoming =
    authenticationStatus === AuthenticationStatus.UPCOMING && !isPast(eta)
  const isAwaitingCheckIn =
    (authenticationStatus === AuthenticationStatus.UPCOMING && isPast(eta)) ||
    (authenticationStatus === AuthenticationStatus.IN_PROGRESS && !isPast(eta))
  const isCheckInOverdue =
    authenticationStatus === AuthenticationStatus.IN_PROGRESS && isPast(eta)
  const isCheckInSuccessful =
    authenticationStatus === AuthenticationStatus.CONFIRMED
  const isManualNoShowRequested =
    authenticationStatus === AuthenticationStatus.MANUAL_NO_SHOW_REQUESTED &&
    isPast(eta)
  const isManualNoShowPending =
    authenticationStatus === AuthenticationStatus.MANUAL_NO_SHOW_PENDING
  const isManualNoShowTriggered =
    authenticationStatus === AuthenticationStatus.NO_SHOW_TRIGGERED
  const isTripCancelled =
    authenticationStatus === AuthenticationStatus.CANCELLED
  const isManualCheckInPending =
    authenticationStatus ===
    AuthenticationStatus.MANUAL_PASSENGER_CHECK_IN_PENDING

  const isDropoff =
    isBoarded &&
    !isAwaitingCheckIn &&
    !isManualNoShowRequested &&
    !isManualNoShowPending &&
    !isManualNoShowTriggered &&
    !isTripCancelled

  if (isUpcoming) {
    return BadgeType.UPCOMING
  }

  if (isAwaitingCheckIn || isManualCheckInPending) {
    return BadgeType.PENDING
  }

  if (isCheckInOverdue || isManualNoShowRequested || isManualNoShowPending) {
    return BadgeType.OVERDUE
  }

  if (isCheckInSuccessful && !isBoarded) {
    return BadgeType.CHECKED_IN
  }

  if (isDropoff) {
    return BadgeType.BOARDED
  }

  if (isManualNoShowTriggered || isNoShow) {
    return BadgeType.NO_SHOW
  }

  if (isTripCancelled && !isNoShow) {
    return BadgeType.CANCELLED
  }

  return BadgeType.NA
}

export enum BadgeType {
  UPCOMING = 'UPCOMING',
  PENDING = 'PENDING',
  OVERDUE = 'OVERDUE',
  CHECKED_IN = 'CHECKED_IN',
  BOARDED = 'BOARDED',
  NO_SHOW = 'NO_SHOW',
  CANCELLED = 'CANCELLED',
  NA = 'NA',
}
